import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IDutyState } from "./types";
import { IIDAStore } from "../types";
import { addDays, addMonths, eachDayOfInterval, endOfDay, isSameDay, startOfDay } from "date-fns";
import { Duty, eSource } from "../../models/duties";
import { dutiesService } from "../../services/Duties.services";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { getIntersectionDatetime, IPeriode } from "../../utils/dateUtils";
import store from "../../store";
import { eRole, User } from "@/modules/security/models/user-model";

export const getInitialState = (): IDutyState => {
  return {
    searching: false,
    duties: new Map(),
    dutiesKeys: new Map(),
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IDutyState, IIDAStore> = {
  GetDuties: (state) => (): Duty[] => {
    return Object.values(state.duties);
  },
  GetDutiesByEmployeeId: (state) => (employeeId: number) => {
    const key = employeeId + "/";
    if (state.dutiesKeys[key]) {
      return Object.values(state.dutiesKeys[key]);
    } else {
      return [];
    }
  },
  GetDutiesByEmployeeIdAndDate:
    (state) =>
    (employeeId: number, dato: Date): Duty[] => {
      const key = employeeId + "/" + startOfDay(dato).getTime();
      if (state.dutiesKeys[key]) {
        return Object.values(state.dutiesKeys[key]);
      } else {
        return [];
      }
    },
  GetDutiesByEmployeeIdAndFromAndToDates:
    (state, getters) =>
    (employeeId: number, from: Date, to: Date): Duty[] => {
      let list: Duty[] = [];
      eachDayOfInterval({ start: from, end: to }).forEach((dato) => {
        const duties = getters.GetDutiesByEmployeeIdAndDate(employeeId, dato);
        if (duties.length > 0) {
          list = list.concat(duties);
        }
      });
      return list.sort((a: Duty, b: Duty) => (a.startDateTime > b.startDateTime ? 1 : -1));
    },
  GetDutiesByPoolId:
    (state, getters) =>
    (poolId: number): Duty[] => {
      const list = getters.GetDuties().filter((a) => a.work_hour_pool_id === poolId);
      return list.sort((a: Duty, b: Duty) => (a.startDateTime > b.startDateTime ? 1 : -1));
    },
  GetDutiesByDate: (state) => (date: Date) => {
    const key = startOfDay(date).getTime();
    if (state.dutiesKeys[key]) {
      return Object.values(state.dutiesKeys[key]);
    } else {
      return [];
    }
  },
  getEventsOnDayPeriode:
    (state, getters) =>
    (start: Date, end: Date, employee_id: number): Duty[] => {
      const list = getters
        .GetDutiesByEmployeeIdAndDate(employee_id, start)
        .filter(
          (d) =>
            d.source === eSource.InstitutionCalendar &&
            getIntersectionDatetime(
              { fra: d.startDateTime, tilOgMed: d.endDateTime },
              { fra: start, tilOgMed: end }
            ) !== undefined
        );
      return list.sort((a: Duty, b: Duty) => (a.startDateTime > b.startDateTime ? 1 : -1));
    },
  getEventsInPeriode:
    (state, getters) =>
    (start: Date, end: Date, employee_id: number): Duty[] => {
      const list = getters
        .GetDutiesByEmployeeIdAndFromAndToDates(employee_id, start, end)
        .filter(
          (d) =>
            d.source === eSource.InstitutionCalendar &&
            getIntersectionDatetime(
              { fra: d.startDateTime, tilOgMed: d.endDateTime },
              { fra: start, tilOgMed: end }
            ) !== undefined
        );
      return list.sort((a: Duty, b: Duty) => (a.startDateTime > b.startDateTime ? 1 : -1));
    }
};

export const actions: ActionTree<IDutyState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchDuties(
    { commit, state, getters },
    query: { from: Date; to: Date; forceUpdate: boolean }
  ): Promise<Duty[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const user: User = store.getters["security/getUser"]();
      const periode = { fra: from, tilOgMed: to } as IPeriode;
      if (query.forceUpdate || !state.storeCache.isCached(periode)) {
        if (query.forceUpdate) {
          commit("reset");
        }
        try {
          dutiesService.fetchDuties(from, to).then((duties) => {
            commit("updateDuties", { duties, from, to });
            return resolve(duties);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const duties = getters
          .GetDuties()
          .filter((ab) => ab.start_date >= from && ab.start_date <= to);
        return resolve(duties);
      }
    });
  },
  fetchDutiesByEventId({ commit, getters }, eventId: number): Promise<Duty[]> {
    return new Promise((resolve, reject) => {
      const duties = getters.GetDuties().filter((d) => d.event_id === eventId);
      if (duties.length > 0) {
        duties.sort((a: Duty, b: Duty) => (a.startDateTime > b.startDateTime ? 1 : -1));
        return resolve(duties);
      } else {
        dutiesService
          .fetchDutiesByEventId(eventId)
          .then((duties) => {
            for (let index = 0; index < duties.length; index++) {
              commit("updateDuty", duties[index]);
            }
            return resolve(duties);
          })
          .catch((error) => {
            return reject(error);
          });
      }
    });
  },
  createDuty({ commit }, duty: Duty): Promise<Duty> {
    return new Promise((resolve, reject) => {
      dutiesService
        .createDuty(duty)
        .then((newDuty) => {
          commit("updateDuty", newDuty);
          resolve(newDuty);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateDuty({ commit }, duty: Duty): Promise<Duty> {
    return new Promise((resolve, reject) => {
      dutiesService
        .updateDuty(duty)
        .then(() => {
          commit("updateDuty", duty);
          resolve(duty);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteDuty({ commit }, dutyId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      dutiesService
        .deleteDuty(dutyId)
        .then((response) => {
          commit("deleteDuty", dutyId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteFutureDuties({ commit }, query: { employeeId: number; fromDate: Date }): Promise<void> {
    return new Promise((resolve) => {
      const duties: Duty[] = store.getters["duties/GetDuties"]();
      const dutyIds = duties
        .filter((d) => d.employee_id === query.employeeId && d.start_date > query.fromDate)
        .map((d) => d.id);
      commit("deleteDuties", dutyIds);
      resolve();
    });
  }
};

export const mutations: MutationTree<IDutyState> = {
  reset(state: IDutyState) {
    Object.assign(state, getInitialState());
  },
  updateDuty(state: IDutyState, duty: Duty) {
    const oldDuty: Duty = state.duties[duty.id];
    if (oldDuty && !isSameDay(startOfDay(oldDuty.start_date), startOfDay(duty.start_date))) {
      // Date have been changed, delete old date
      delete state.dutiesKeys[oldDuty.employee_id + "/" + startOfDay(oldDuty.start_date).getTime()][
        duty.id
      ];
    }
    state.duties[duty.id] = duty;
    setKey(state.dutiesKeys, duty.employee_id + "/" + startOfDay(duty.start_date).getTime(), duty);
    setKey(state.dutiesKeys, duty.employee_id + "/", duty);
    setKey(state.dutiesKeys, startOfDay(duty.start_date).getTime(), duty);
  },
  deleteDuty(state: IDutyState, dutyId: number) {
    const oldDuty = state.duties[dutyId];
    if (oldDuty) {
      delete state.dutiesKeys[oldDuty.employee_id + "/" + startOfDay(oldDuty.start_date).getTime()][
        dutyId
      ];
      delete state.dutiesKeys[oldDuty.employee_id + "/"][dutyId];
      delete state.dutiesKeys[startOfDay(oldDuty.start_date).getTime()][dutyId];
    }

    delete state.duties[dutyId];
  },
  deleteDuties(state: IDutyState, dutyIds: number[]) {
    for (let index = 0; index < dutyIds.length; index++) {
      const dutyId = dutyIds[index];
      const oldDuty = state.duties[dutyId];
      if (oldDuty) {
        delete state.dutiesKeys[
          oldDuty.employee_id + "/" + startOfDay(oldDuty.start_date).getTime()
        ][dutyId];
        delete state.dutiesKeys[oldDuty.employee_id + "/"][dutyId];
        delete state.dutiesKeys[startOfDay(oldDuty.start_date).getTime()][dutyId];
      }

      delete state.duties[dutyId];
    }
  },
  updateDuties(
    state: IDutyState,
    payload: { duties: Duty[]; from: Date | undefined; to: Date | undefined }
  ) {
    for (let index = 0; index < payload.duties.length; index++) {
      const duty = Object.freeze(payload.duties[index]);
      store.commit("duties/updateDuty", duty);
    }
    if (payload.from && payload.to) {
      state.storeCache.addCache(new storeCachePeriode({ fra: payload.from, tilOgMed: payload.to }));
    }
  }
};

export const dutiesStoreModule: Module<IDutyState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

function setKey(statevalue, key, value) {
  const valueFromKey = statevalue[key];
  if (!valueFromKey) {
    statevalue[key] = { [value.id]: value };
  } else {
    valueFromKey[value.id] = value;
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export class StoreDuties {
  // eslint-disable-next-line no-inner-declarations
  static fetchDuties(firstDay: Date, lastDay: Date | undefined): Promise<Duty[]> {
    return store.dispatch("duties/fetchDuties", {
      from: firstDay,
      to: lastDay
    });
  }
}
